import { observer } from 'mobx-react';
import React, { useCallback } from 'react';

import { useStores } from 'core';
import { DataGrid, DataGridFilters, GridRef, DataGridSorting } from 'shared/components/other';
import { LogFilters } from 'types/cabinetCopying';
import { SortingOptions } from 'types';

import { convertGridFiltersToApiFilters, getColumns, FILTER_ROW_HEIGHT, defaultSorting } from '../../../../helpers/log';

type Props = {
  gridRef: React.RefObject<GridRef['current']>;
  className?: string;
};

// Adapter to convert between DataGridSorting and SortingOptions
const convertToSortingOptions = (sorting: DataGridSorting): SortingOptions => {
  if (!sorting) return null;
  return {
    field: sorting.columnKey,
    direction: sorting.direction === 'asc' ? 'ASC' : 'DESC',
  };
};

// Adapter to convert SortingOptions to DataGridSorting
const convertToDataGridSorting = (sorting: SortingOptions): DataGridSorting => {
  if (!sorting) return null;
  return {
    columnKey: sorting.field,
    direction: sorting.direction === 'ASC' ? 'asc' : 'desc',
  };
};

export const LogTable: React.FC<Props> = observer(({ gridRef, className }) => {
  const { cabinetCopyingStore: store } = useStores();
  const { data, setFilters, setSorting } = store.childrenStores.log;

  const handleFilterChange = useCallback(
    (filters: DataGridFilters<keyof LogFilters>) => {
      setFilters(convertGridFiltersToApiFilters(filters));
    },
    [setFilters],
  );

  const handleSortingChange = useCallback(
    (sorting: DataGridSorting) => {
      setSorting(convertToSortingOptions(sorting));
    },
    [setSorting],
  );

  return (
    <DataGrid
      className={className}
      gridRef={gridRef}
      filterRowHeight={FILTER_ROW_HEIGHT}
      rows={data}
      columns={getColumns(store.options.cabinet.items)}
      onFilterChange={handleFilterChange}
      onSortingChange={handleSortingChange}
      enableVirtualization={false}
      initialSorting={convertToDataGridSorting(defaultSorting)}
    />
  );
});
